// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Right-click context menu.
export default class extends Controller {
  static outlets = ["ui--flow"]

  connect() {
    this._onCtx = (e) => this._show(e)
    this._onClickOutside = (e) => this._hide(e)
    this._onKeydown = (e) => this._onMenuKeydown(e)

    if (this.hasUiFlowOutlet) {
      this.uiFlowOutlet.element.addEventListener("ui--flow:contextmenu", this._onCtx)
    }
    document.addEventListener("click", this._onClickOutside)
    this.element.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    this.element.removeEventListener("keydown", this._onKeydown)
    if (this.hasUiFlowOutlet) {
      this.uiFlowOutlet.element.removeEventListener("ui--flow:contextmenu", this._onCtx)
    }
  }

  _show(event) {
    event.preventDefault()
    event.stopPropagation()
    const { x, y, nodeId, flowPosition } = event.detail
    this.currentNodeId = nodeId
    this.currentFlowPosition = flowPosition
    this.element.style.display = "block"
    // detail.x/y are raw clientX/clientY (flow_controller._handleContextMenu),
    // but this menu is `absolute` inside the `position: relative` canvas — the
    // coordinates must be canvas-relative or the menu lands offset by the
    // canvas's page position on every real page (jsdom's zero rects hide it).
    const rect = this._canvasElement()?.getBoundingClientRect()
    this.element.style.left = `${x - (rect?.left || 0)}px`
    this.element.style.top = `${y - (rect?.top || 0)}px`

    // Menu pattern (a11y): remember what had focus so Escape can hand it
    // back, then move focus to the first item — a role=menu that opens with
    // focus left outside is invisible to keyboard users.
    this._opener = document.activeElement
    const first = this._menuItems()[0]
    if (first) first.focus()
  }

  _hide(event) {
    if (this.element.style.display === "none") return
    if (event && this.element.contains(event.target)) return
    this.element.style.display = "none"
    // Focus must not survive inside a display:none container — it would fall
    // back to <body> and be lost for keyboard users.
    if (this.element.contains(document.activeElement)) document.activeElement.blur()
  }

  // Menu pattern (a11y): ArrowUp/ArrowDown rove the menuitems (wrapping, from
  // the container ArrowUp lands on the last item), Home/End jump, Escape
  // closes and restores focus to the element that opened the menu.
  // stopPropagation on Escape keeps the engine's document-level Escape
  // (clearSelection + cancelConnecting) out of a menu-close keystroke.
  _onMenuKeydown(event) {
    if (this.element.style.display === "none") return
    const items = this._menuItems()
    if (items.length === 0) return
    const index = items.indexOf(document.activeElement)

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        items[(index + 1) % items.length].focus()
        break
      case "ArrowUp": {
        event.preventDefault()
        const prev = index === -1 ? items.length - 1 : (index - 1 + items.length) % items.length
        items[prev].focus()
        break
      }
      case "Home":
        event.preventDefault()
        items[0].focus()
        break
      case "End":
        event.preventDefault()
        items[items.length - 1].focus()
        break
      case "Escape":
        event.preventDefault()
        event.stopPropagation()
        this._hide()
        this._restoreFocusToOpener()
        break
    }
  }

  _menuItems() {
    return [...this.element.querySelectorAll("[role='menuitem']")]
  }

  // The positioned ancestor the menu's absolute left/top resolve against: the
  // flow canvas (the component renders the menu inside it). Outlet first —
  // the closest() fallback covers markup where the outlet has not connected.
  _canvasElement() {
    try { if (this.hasUiFlowOutlet) return this.uiFlowOutlet.element } catch (e) { }
    return this.element.closest("[data-controller~='ui--flow']")
  }

  _restoreFocusToOpener() {
    if (this._opener && this._opener.isConnected) this._opener.focus()
    this._opener = null
  }

  deleteNode() {
    if (this.currentNodeId) this.element.dispatchEvent(new CustomEvent("ui--flow:delete", { detail: { nodeId: this.currentNodeId }, bubbles: true }))
    this._hide()
  }

  duplicateNode() {
    if (this.currentNodeId) this.element.dispatchEvent(new CustomEvent("ui--flow:duplicate", { detail: { nodeId: this.currentNodeId }, bubbles: true }))
    this._hide()
  }

  addNodeHere() {
    this.element.dispatchEvent(new CustomEvent("ui--flow:addnode", { detail: { x: this.currentFlowPosition?.x || 0, y: this.currentFlowPosition?.y || 0 }, bubbles: true }))
    this._hide()
  }

  // Host-owned items (G3): the engine consumes the built-ins above; anything
  // else belongs to the host. A menu item wired as
  // `click->ui--flow-context-menu#emit` with a `data-event-name="..."`
  // attribute dispatches `ui--flow:<name>` on the FLOW element (so host
  // listeners on the canvas see it) carrying the menu's current context —
  // { nodeId, flowPosition } — then closes like a built-in. Hosts that build
  // items through ContextMenuComponent get this wiring for free from the
  // `event:` item kwarg (it renders `#emit` + `data-event-name`); hand-authored
  // markup can point `:action` at `emit` and add the data attribute directly.
  emit(event) {
    const name = event?.currentTarget?.dataset?.eventName
    if (!name) return
    const flow = this.hasUiFlowOutlet ? this.uiFlowOutlet : null
    ;(flow?.element || this.element).dispatchEvent(new CustomEvent(`ui--flow:${name}`, {
      detail: { nodeId: this.currentNodeId || null, flowPosition: this.currentFlowPosition || null },
      bubbles: true
    }))
    this._hide()
  }
}
